import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { extractErrorMessage } from '../../../api/errors';
import { marketplaceApi } from '../../../api/marketplace';
import { organisationsApi } from '../../../api/organisations';
import Button from '../../../components/ui/Button';
import Card from '../../../components/ui/Card';
import Input from '../../../components/ui/Input';
import { useCart } from './CartContext';

function formatCurrency(n) {
  return `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
}

function ListingCard({ listing, onAdd }) {
  const [quantity, setQuantity] = useState(String(listing.min_order_quantity));
  const total = Number(quantity || 0) * Number(listing.price_per_unit);
  const cover = listing.images && listing.images[0];

  return (
    <Card className="flex flex-col gap-3 overflow-hidden p-0">
      <Link to={`/app/listings/${listing.id}`} className="block">
        {cover ? (
          <img
            src={cover.image}
            alt={listing.title}
            className="h-40 w-full object-cover transition-transform duration-300 hover:scale-[1.02]"
            loading="lazy"
          />
        ) : (
          <div className="flex h-40 w-full items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100 text-3xl text-green-700/40">
            🌾
          </div>
        )}
      </Link>

      <div className="flex flex-1 flex-col gap-3 px-5 pb-5">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-green-700">
          {listing.crop_name}
        </p>
        <Link to={`/app/listings/${listing.id}`} className="block">
          <h3 className="mt-1 text-base font-semibold text-gray-900 hover:text-green-800">{listing.title}</h3>
        </Link>
        <p className="mt-0.5 text-xs text-gray-500">
          {listing.organisation_name} · {listing.organisation_district}, {listing.organisation_state}
        </p>
      </div>

      {listing.description && (
        <p className="text-xs leading-relaxed text-gray-600 line-clamp-3">{listing.description}</p>
      )}

      <div className="grid grid-cols-2 gap-2 rounded-lg bg-gray-50 p-3 text-xs">
        <div>
          <p className="text-gray-500">Price</p>
          <p className="font-semibold text-gray-900">{formatCurrency(listing.price_per_unit)} / {listing.unit}</p>
        </div>
        <div>
          <p className="text-gray-500">Available</p>
          <p className="font-semibold text-gray-900">{Number(listing.quantity_available).toFixed(2)} {listing.unit}</p>
        </div>
        <div>
          <p className="text-gray-500">Min order</p>
          <p className="font-semibold text-gray-900">{Number(listing.min_order_quantity).toFixed(2)} {listing.unit}</p>
        </div>
        <div>
          <p className="text-gray-500">Available from</p>
          <p className="font-semibold text-gray-900">{listing.available_from}</p>
        </div>
      </div>

      <div className="mt-auto flex items-end gap-2">
        <Input
          label={`Quantity (${listing.unit})`}
          type="number"
          step="0.01"
          min={listing.min_order_quantity}
          max={listing.quantity_available}
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          className="flex-1"
        />
        <Button onClick={() => onAdd(listing, quantity)} size="md" className="mb-[1px]">
          Add · {formatCurrency(total)}
        </Button>
      </div>

      <Link
        to={`/app/listings/${listing.id}`}
        className="text-xs font-semibold text-green-700 hover:text-green-800"
      >
        View full details →
      </Link>
      </div>
    </Card>
  );
}

export default function Marketplace() {
  const { addItem } = useCart();
  const [listings, setListings] = useState([]);
  const [crops, setCrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ q: '', crop: '', state: '' });
  const [adding, setAdding] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.q) params.q = filters.q;
      if (filters.crop) params.crop = filters.crop;
      if (filters.state) params.state = filters.state;
      const [l, c] = await Promise.all([
        marketplaceApi.browse(params),
        crops.length ? Promise.resolve(crops) : organisationsApi.listCrops(),
      ]);
      setListings(l);
      setCrops(c);
      setError('');
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    load();
  };

  const handleAdd = async (listing, quantity) => {
    const qty = Number(quantity);
    if (!Number.isFinite(qty) || qty <= 0) {
      window.alert('Enter a valid quantity.');
      return;
    }
    if (qty < Number(listing.min_order_quantity)) {
      window.alert(`Minimum order is ${listing.min_order_quantity} ${listing.unit}.`);
      return;
    }
    if (qty > Number(listing.quantity_available)) {
      window.alert(`Only ${listing.quantity_available} ${listing.unit} available.`);
      return;
    }
    setAdding(listing.id);
    addItem(listing, qty);
    setTimeout(() => setAdding(null), 600);
  };

  const states = useMemo(
    () => Array.from(new Set(listings.map((l) => l.organisation_state).filter(Boolean))).sort(),
    [listings],
  );

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Marketplace</h1>
        <p className="mt-1 text-sm text-gray-600">
          Browse published crop listings from Farmer Producer Organisations. Direct supply, transparent pricing.
        </p>
      </div>

      <Card className="p-4">
        <form onSubmit={handleSearch} className="grid gap-3 sm:grid-cols-[1fr_auto_auto_auto]">
          <Input
            placeholder="Search by title…"
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
          />
          <select
            value={filters.crop}
            onChange={(e) => setFilters({ ...filters, crop: e.target.value })}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-200"
          >
            <option value="">All crops</option>
            {crops.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select
            value={filters.state}
            onChange={(e) => setFilters({ ...filters, state: e.target.value })}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-200"
          >
            <option value="">All states</option>
            {states.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <Button type="submit">Search</Button>
        </form>
      </Card>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {loading ? (
        <p className="text-sm text-gray-500">Loading listings…</p>
      ) : listings.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-sm text-gray-500">No listings match your filters.</p>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {listings.map((l) => (
            <ListingCard key={l.id} listing={l} onAdd={handleAdd} />
          ))}
        </div>
      )}

      {adding && (
        <div className="fixed bottom-6 left-1/2 z-40 -translate-x-1/2 rounded-lg bg-green-700 px-4 py-2 text-sm font-semibold text-white shadow-lg">
          Added to cart ✓
        </div>
      )}
    </div>
  );
}
